import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { BlobProvider, Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import logo from 'images/hostplatform-logo.png';
import { getPricingModelByHostId } from 'utils/apis/host';
import moment from 'moment';
import { capitalizeFirstLetter } from 'utils/general';
import SubscriptionInvoicePDF from './PDFs/SubscriptionInvoicePDF';
import HostProtectInvoicePDF from './PDFs/HostProtectInvoicePDF';

// Define styles for the PDF
function addSpaceBetweenEmojiAndChinese(text) {
  // Function to check if a character is Chinese
  function isChinese(char) {
    return /[\u4e00-\u9fff]/.test(char);
  }

  // regex to check if a character is likely an emoji
  function isLikelyEmoji(char) {
    return /[\u{1F300}-\u{1F5FF}\u{1F900}-\u{1F9FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E6}-\u{1F1FF}\u{1F191}-\u{1F251}\u{1F004}\u{1F0CF}\u{1F170}-\u{1F171}\u{1F17E}-\u{1F17F}\u{1F18E}\u{3030}\u{2B50}\u{2B55}\u{2934}-\u{2935}\u{2B05}-\u{2B07}\u{2B1B}-\u{2B1C}\u{3297}\u{3299}\u{303D}\u{00A9}\u{00AE}\u{2122}\u{23F3}\u{24C2}\u{23E9}-\u{23EF}\u{25B6}\u{23F8}-\u{23FA}]/u.test(
      char
    );
  }

  let result = '';
  let prevType = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    let currentType = '';

    if (isChinese(char)) {
      currentType = 'chinese';
    } else if (isLikelyEmoji(char)) {
      currentType = 'emoji';
    } else {
      currentType = 'other';
    }

    // Add space if transitioning between different types
    if (prevType && prevType !== currentType && (prevType === 'chinese' || currentType === 'chinese')) {
      result += ' ';
    }

    result += char;
    prevType = currentType;
  }

  return result;
}

const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

// Define the InvoicePDF component
const InvoicePDF = ({
  host,
  inventories,
  additionalProducts,
  hostProtectProducts,
  date,
  invoiceRef,
  remark,
  excludeSystemSubscription,
  chargeableChild,
  modifiedSubFee,
  discountApplied,
  onGenerate,
  subscriptionType,
  subscriptionMonth,
  hostName,
  waivedUnit,
  hasParentChildHost
}) => {
  const [filteredInventories, setFilteredInventories] = useState([]);
  const [systemProductData, setSystemProductData] = useState([]);
  const [addedProducts, setAddedProducts] = useState([]);
  const [hostProtectProductsData, setHostProtectProductsData] = useState([]);

  const [modifiedInventories, setModifiedInventories] = useState([]);
  const [inventoryChunks, setInventoryChunks] = useState([]);

  const [taxesApplied, setTaxesApplied] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [systemProductSubTotal, setSystemProductSubTotal] = useState();
  const [subTotalAfterDiscount, setSubTotalAfterDiscount] = useState();
  const [discount, setDiscount] = useState({});
  const [subscriptionDesc, setSubscriptionDesc] = useState('');
  const [priceTier, setPriceTier] = useState('');

  const [grandTotal, setGrandTotal] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false); // Clean up when the component is unmounted
  }, []);

  const determinePricingTier = (pricingCriteria, count) => {
    for (const [tier, range] of Object.entries(pricingCriteria)) {
      // if the range.maxUnit is null, then its > minUnit
      if (count >= range.minUnit && (!range.maxUnit || count <= range.maxUnit)) {
        return tier;
      }
    }
    return null;
  };

  const calculateSubscriptionPricing = (pricingTable, product, tier) => {
    const productPricing = pricingTable.products.find(p => p.product.toString() === product._id.toString());
    if (productPricing && productPricing.tiers) {
      // turn into array  [tier1 , {pricing}]
      const tiersArray = Object.entries(productPricing.tiers);
      const matchingTier = tiersArray.find(([key, _]) => key === tier);
      if (matchingTier) {
        return matchingTier[1];
      }
    }
    return null;
  };

  const calculateAddedProductPricing = (selectedProduct, otherProductsFromPricingModel) => {
    const productPricing = otherProductsFromPricingModel.find(p => p.product.toString() === selectedProduct._id.toString());
    if (productPricing) {
      return productPricing.price;
    }

    return null;
  };

  const processInventories = (inventories, modifiedSubFee) => {
    const validInventories = inventoriesValidation(inventories);
    const modified = [];
    const filtered = [];

    validInventories.forEach(inventory => {
      if (modifiedSubFee && modifiedSubFee[inventory.unitId]) {
        modified.push({
          ...inventory,
          unitConfig: {
            ...inventory.unitConfig,
            subscriptionFee: modifiedSubFee[inventory.unitId]
          }
        });
      } else {
        filtered.push(inventory);
      }
    });

    return { filtered, modified };
  };

  const inventoriesValidation = inventories =>
    //add more validation in the future
    inventories.filter(
      inventory =>
        !inventory.isUnitDeleted &&
        !inventory.unitConfig.isFree &&
        (inventory.unitConfig.unitType === 'live' || inventory.unitConfig.unitType === 'ghost' || inventory.unitConfig.unitType === 'demo')
    );

  const inventoriesChargeableChildValidation = inventories => inventories.filter(inventory => !inventory.unitParent);

  const inventoriesWaivedValidation = (inventories, waivedUnit) => {
    return inventories.filter(inventory => !waivedUnit.includes(inventory.unitId));
  };

  const inventoriesUnitCreatedValidation = inventories => {
    // Get the current date
    const now = new Date();
    const currentMonth = now.getMonth(); // (0 = January, 1 = February, ..., 11 = December)
    const currentYear = now.getFullYear();

    // Filter the inventories
    return inventories.filter(unit => {
      const createdAt = new Date(unit.unitCreatedAt);
      const createdMonth = createdAt.getMonth();
      const createdYear = createdAt.getFullYear();

      // Return true if the unit was not created in the current month and year
      return createdMonth !== currentMonth || createdYear !== currentYear;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const pricingModel = await getPricingModelByHostId(host);
        const { taxes, allTax, pricingTable, products, otherProducts } = pricingModel.data;

        console.log(pricingModel.data);

        //filter inventories for validations.
        let filteredInventories = inventoriesValidation(inventories);

        setFilteredInventories(filteredInventories);

        //console.log(inventories, 12);
        //taxes
        const taxesApplied = allTax.filter(tax => taxes.some(t => t === tax._id));
        setTaxesApplied(taxesApplied);

        //product
        let subTotal = 0;
        let systemProductSubTotal = 0;
        let addedProductSubTotal = 0;
        let hostProtectSubTotal = 0;
        let childUnits = [];

        let systemProducts;
        let modifiedInventoriesData = [];

        if (excludeSystemSubscription === false) {
          if (waivedUnit.length > 0) {
            filteredInventories = inventoriesWaivedValidation(filteredInventories, waivedUnit);
          }
          if (chargeableChild === false) {
            filteredInventories = inventoriesChargeableChildValidation(filteredInventories);
          }
          childUnits = inventories.filter(inventory => {
            return !filteredInventories.some(filtered => filtered.unitId === inventory.unitId);
          });
          if (subscriptionType === 'monthly') {
            filteredInventories = inventoriesUnitCreatedValidation(filteredInventories);
            childUnits = inventories.filter(inventory => {
              return !filteredInventories.some(filtered => filtered.unitId === inventory.unitId);
            });

            const { filtered, modified } = processInventories(filteredInventories, modifiedSubFee);
            setFilteredInventories(filtered);

            //console.log('Filtered Inventories:', filtered);
            //console.log('Modified Inventories:', modified);

            const productInventoryMap = {};
            const modifiedProductInventoryMap = {};
            let totalInventoryCount = 0;

            if (modified.length > 0) {
              modified.forEach(inventory => {
                const productName = inventory.planName.toLowerCase();
                const product = products.find(p => p.name.toLowerCase() === productName);
                const subscriptionFee = inventory.unitConfig.subscriptionFee; // Check if subscriptionFee exists

                if (product) {
                  if (subscriptionFee) {
                    // Handle as subFee
                    const key = `${productName}-${subscriptionFee}`;
                    if (!modifiedProductInventoryMap[key]) {
                      modifiedProductInventoryMap[key] = {
                        ...product,
                        subscriptionFee,
                        inventoryCount: 0
                      };
                    }
                    modifiedProductInventoryMap[key].inventoryCount += 1;
                    totalInventoryCount += 1;
                  }
                }
              });
            }

            filtered.forEach(inventory => {
              const productName = inventory.planName.toLowerCase();
              const product = products.find(p => p.name.toLowerCase() === productName);

              if (product) {
                if (!productInventoryMap[productName]) {
                  productInventoryMap[productName] = {
                    ...product,
                    inventoryCount: 0
                  };
                }
                productInventoryMap[productName].inventoryCount += 1;
                totalInventoryCount += 1;
              }
            });

            //console.log(inventories);

            // Determine pricing tier and calculate prices
            // return tier1, tier2, ...
            const pricingTier = determinePricingTier(pricingTable.pricingCriteria, totalInventoryCount);
            const numericTier = pricingTier.replace('tier', ''); // Remove the 'tier' prefix
            setPriceTier(numericTier);
            if (modified.length > 0) {
              // Calculate modified inventories data with totalPrice based on subscriptionFee
              modifiedInventoriesData = Object.values(modifiedProductInventoryMap).map(product => {
                return {
                  ...product,
                  totalPrice: product.subscriptionFee * product.inventoryCount
                };
              });
              systemProductSubTotal = modifiedInventoriesData.reduce((acc, product) => acc + product.totalPrice, systemProductSubTotal);
            }

            systemProducts = Object.values(productInventoryMap).map(product => {
              const pricing = calculateSubscriptionPricing(pricingTable, product, pricingTier);
              //put the subFee based on each unit ?
              const updatedInventories = inventories.map(inventory => {
                if (inventory.planNameLabel === product.name) {
                  if (waivedUnit.some(waived => waived.unitId === inventory.unitId) || childUnits.some(child => child.unitId === inventory.unitId)) {
                    return {
                      ...inventory,
                      unitName: addSpaceBetweenEmojiAndChinese(inventory.unitName),
                      unitConfig: {
                        ...inventory.unitConfig,
                        subscriptionFee: 0,
                        pricingModelSubFee: 0
                      }
                    };
                  } else {
                    return {
                      ...inventory,
                      unitName: addSpaceBetweenEmojiAndChinese(inventory.unitName),
                      unitConfig: {
                        ...inventory.unitConfig,
                        pricingModelSubFee: pricing.monthPrice
                      }
                    };
                  }
                }
                return inventory;
              });

              inventories = updatedInventories;
              return {
                ...product,
                pricing: pricing.monthPrice,
                totalPrice: pricing.monthPrice * product.inventoryCount
              };
            });

            //Ensure that invalid inventory subFee is 0 !
            const updatedInventories = inventories.map(inventory => {
              // Check if the inventory's planNameLabel matches the product name
              // Check if the inventory's unitId is in either waivedUnit or childUnits
              if (waivedUnit.some(waived => waived.unitId === inventory.unitId) || childUnits.some(child => child.unitId === inventory.unitId)) {
                return {
                  ...inventory,
                  unitName: addSpaceBetweenEmojiAndChinese(inventory.unitName),
                  unitConfig: {
                    ...inventory.unitConfig,
                    subscriptionFee: 0, // Set subscriptionFee to 0
                    pricingModelSubFee: 0 // Set pricingModelSubFee to 0
                  }
                };
              }
              // Return the inventory unchanged if it doesn't meet the condition
              return inventory;
            });

            inventories = updatedInventories;

            setModifiedInventories(modifiedInventoriesData);
            setSystemProductData(systemProducts);
            //after seperating normal and modified one, tally price tgt
            systemProductSubTotal = systemProducts.reduce((acc, product) => acc + product.totalPrice, systemProductSubTotal);
            setSystemProductSubTotal(systemProductSubTotal);
          } else if (subscriptionType === 'yearly') {
            const { filtered, modified } = processInventories(filteredInventories, modifiedSubFee);
            setFilteredInventories(filtered);

            const productInventoryMap = {};
            const modifiedProductInventoryMap = {};
            let totalInventoryCount = 0;

            if (modified.length > 0) {
              modified.forEach(inventory => {
                const productName = inventory.planName.toLowerCase();
                const product = products.find(p => p.name.toLowerCase() === productName);
                const subscriptionFee = inventory.unitConfig.subscriptionFee; // Check if subscriptionFee exists
                const unitCreatedAt = moment(inventory.unitCreatedAt);

                if (product && subscriptionFee) {
                  const currentYear = moment().year(); // custom this 2024,2025
                  const currentMonth = moment().month(); // 0 = January, 11 = December

                  // Determine the year to use for currentDate based on subscriptionMonth
                  // subscriptionMonth + 1, so that if lets say invoice issue at Aug, at Sept we still able to see it as Aug in Invoice (it wont go to next year !)
                  // if subMonth = 11, 12 >= currentMonth always true,
                  const yearToUse =
                    subscriptionMonth === 11
                      ? currentMonth === 0
                        ? currentYear - 1
                        : currentYear
                      : subscriptionMonth + 1 >= currentMonth
                      ? currentYear
                      : currentYear + 1;

                  // Check if the unit was created in the current month and year
                  const unitCreatedYear = unitCreatedAt.year();
                  const unitCreatedMonth = unitCreatedAt.month();
                  //if subscriptionMonth is December, then if unit was created at January, then we wouldnt count that !
                  if (
                    (subscriptionMonth === 11 && unitCreatedYear === yearToUse + 1 && unitCreatedMonth === 0) ||
                    (unitCreatedYear === yearToUse && unitCreatedMonth === subscriptionMonth + 1)
                  ) {
                    inventory.unitConfig.subscriptionFee = 0;
                    inventory.unitConfig.pricingModelSubFee = 0;
                    childUnits.push(inventory);
                    return;
                  }

                  // Set currentDate to the first day of the subscriptionMonth in the determined year
                  const currentDate = moment({ year: yearToUse, month: subscriptionMonth }).startOf('month');

                  // Set the end date based on the subscriptionMonth and calculated year
                  const endDate = moment({ year: yearToUse, month: subscriptionMonth })
                    .endOf('month')
                    .format('DD/MM/YYYY');

                  const endDateMoment = moment(endDate, 'DD/MM/YYYY');

                  // Calculate the monthGap between endDate and unitCreatedAt
                  let monthGap = endDateMoment.diff(unitCreatedAt, 'months') + 1; // Include start/end month
                  monthGap = Math.min(monthGap, 12); // Cap the monthGap at 12 months

                  const key = `${productName}-${monthGap}-${subscriptionFee}`;

                  if (!modifiedProductInventoryMap[key]) {
                    // Calculate startDate based on the monthGap
                    const startDate = currentDate
                      .subtract(monthGap - 1, 'months') // Subtract monthGap to get the start date
                      .startOf('month')
                      .format('DD/MM/YYYY');

                    modifiedProductInventoryMap[key] = {
                      ...product,
                      subscriptionFee,
                      monthGap,
                      inventoryCount: 0,
                      totalPrice: 0, // Initialize totalPrice
                      description: `Subscription Period: ${startDate} to ${endDate}`
                    };
                  }
                  modifiedProductInventoryMap[key].inventoryCount += 1;
                  modifiedProductInventoryMap[key].totalPrice += subscriptionFee; // Accumulate totalPrice
                  totalInventoryCount += 1;
                }
              });
            }

            filtered.forEach(inventory => {
              const productName = inventory.planName.toLowerCase();
              const product = products.find(p => p.name.toLowerCase() === productName);

              if (product) {
                if (!productInventoryMap[productName]) {
                  productInventoryMap[productName] = {
                    ...product,
                    inventoryCount: 0
                  };
                }
                productInventoryMap[productName].inventoryCount += 1;
                totalInventoryCount += 1;
              }
            });

            const pricingTier = determinePricingTier(pricingTable.pricingCriteria, totalInventoryCount);

            if (modified.length > 0) {
              // Calculate modified inventories data with totalPrice based on subscriptionFee
              modifiedInventoriesData = Object.values(modifiedProductInventoryMap).map(product => {
                return {
                  ...product,
                  totalPrice: product.subscriptionFee * product.inventoryCount
                };
              });
              systemProductSubTotal = modifiedInventoriesData.reduce((acc, product) => acc + product.totalPrice, systemProductSubTotal);
            }

            systemProducts = Object.values(productInventoryMap).map(product => {
              const pricing = calculateSubscriptionPricing(pricingTable, product, pricingTier);
              const numericTier = pricingTier.replace('tier', ''); // Remove the 'tier' prefix
              setPriceTier(numericTier);

              const productGroupMap = {}; // Group products by planNameLabel and monthGap

              filtered.forEach(inventory => {
                if (inventory.planNameLabel === product.name) {
                  const unitCreatedAt = moment(inventory.unitCreatedAt);

                  // Determine the current year and month based on subscriptionMonth
                  const currentYear = moment().year(); // custom this 2024,2025
                  const currentMonth = moment().month(); // 0 = January, 11 = December

                  // Determine the year to use for currentDate based on subscriptionMonth
                  // subscriptionMonth + 1, so that if lets say invoice issue at Aug, at Sept we still able to see it as Aug in Invoice (it wont go to next year !)
                  // if subMonth = 11, 12 >= currentMonth always true,
                  const yearToUse =
                    subscriptionMonth === 11
                      ? currentMonth === 0
                        ? currentYear - 1
                        : currentYear
                      : subscriptionMonth + 1 >= currentMonth
                      ? currentYear
                      : currentYear + 1;

                  // Check if the unit was created in the current month and year
                  const unitCreatedYear = unitCreatedAt.year();
                  const unitCreatedMonth = unitCreatedAt.month();
                  //if subscriptionMonth is December, then if unit was created at January, then we wouldnt count that !
                  if (
                    (subscriptionMonth === 11 && unitCreatedYear === yearToUse + 1 && unitCreatedMonth === 0) ||
                    (unitCreatedYear === yearToUse && unitCreatedMonth === subscriptionMonth + 1)
                  ) {
                    inventory.unitName = addSpaceBetweenEmojiAndChinese(inventory.unitName);
                    inventory.unitConfig.subscriptionFee = 0;
                    inventory.unitConfig.pricingModelSubFee = 0;
                    return;
                  }

                  // Set currentDate to the first day of the subscriptionMonth in the determined year
                  const currentDate = moment({ year: yearToUse, month: subscriptionMonth }).startOf('month');

                  // Set the end date based on the subscriptionMonth and calculated year
                  const endDate = moment({ year: yearToUse, month: subscriptionMonth })
                    .endOf('month')
                    .format('DD/MM/YYYY');

                  const endDateMoment = moment(endDate, 'DD/MM/YYYY');

                  // Calculate the monthGap between endDate and unitCreatedAt
                  let monthGap = endDateMoment.diff(unitCreatedAt, 'months') + 1; // Include start/end month
                  monthGap = Math.min(monthGap, 12); // Cap the monthGap at 12 months

                  // Calculate startDate based on the monthGap
                  const startDate = currentDate
                    .subtract(monthGap - 1, 'months') // Subtract monthGap to get the start date
                    .startOf('month')
                    .format('DD/MM/YYYY');

                  const key = `${product.name}-${monthGap}`;

                  if (!productGroupMap[key]) {
                    productGroupMap[key] = {
                      ...product,
                      pricing: {},
                      totalPrice: 0,
                      inventoryCount: 0,
                      monthGap,
                      description: `Subscription Period: ${startDate} to ${endDate}`
                    };

                    // Append the subscription period to the description only once
                    productGroupMap[key].pricing = monthGap === 12 ? pricing.yearPrice : pricing.monthPrice * monthGap;
                  }

                  if (childUnits.some(child => child.unitId === inventory.unitId)) {
                    inventory.unitName = addSpaceBetweenEmojiAndChinese(inventory.unitName);
                    inventory.unitConfig.subscriptionFee = 0;
                    inventory.unitConfig.pricingModelSubFee = 0;
                  } else {
                    productGroupMap[key].inventoryCount += 1;
                    inventory.unitName = addSpaceBetweenEmojiAndChinese(inventory.unitName);
                    // List price change here
                    inventory.unitConfig.pricingModelSubFee = monthGap === 12 ? pricing.yearPrice : pricing.monthPrice * monthGap;
                    productGroupMap[key].totalPrice += inventory.unitConfig.pricingModelSubFee;
                  }
                }
              });

              return Object.values(productGroupMap).map(group => ({
                ...group
              }));
            });

            const updatedInventories = inventories.map(inventory => {
              // Check if the inventory's planNameLabel matches the product name
              // Check if the inventory's unitId is in either waivedUnit or childUnits
              if (waivedUnit.some(waived => waived.unitId === inventory.unitId) || childUnits.some(child => child.unitId === inventory.unitId)) {
                return {
                  ...inventory,
                  unitName: addSpaceBetweenEmojiAndChinese(inventory.unitName),
                  unitConfig: {
                    ...inventory.unitConfig,
                    subscriptionFee: 0, // Set subscriptionFee to 0
                    pricingModelSubFee: 0 // Set pricingModelSubFee to 0
                  }
                };
              }
              // Return the inventory unchanged if it doesn't meet the condition
              return inventory;
            });

            inventories = updatedInventories;

            // Flatten the array of grouped products
            systemProducts = systemProducts.flat();
            //console.log(inventories, 12)

            setModifiedInventories(modifiedInventoriesData);
            setSystemProductData(systemProducts);
            systemProductSubTotal = systemProducts.reduce((acc, product) => acc + product.totalPrice, systemProductSubTotal);
            setSystemProductSubTotal(systemProductSubTotal);
          }
        }

        const hostProtectProductData =
          Array.isArray(hostProtectProducts) && hostProtectProducts.length > 0
            ? hostProtectProducts.reduce(
                (acc, hostProtectProduct) => {
                  const product = products.find(p => p._id.toString() === hostProtectProduct.code.toString());

                  if (product) {
                    const pricing = calculateAddedProductPricing(product, otherProducts);

                    acc.quantity += 1;
                    acc.totalPrice += pricing;
                    acc.hostProtectUnits.push(hostProtectProduct);

                    if (!acc.productDetails) {
                      acc.productDetails = { ...product };
                      acc.pricing = pricing;
                    }
                  }

                  return acc;
                },
                {
                  productDetails: null,
                  pricing: 0,
                  quantity: 0,
                  totalPrice: 0,
                  hostProtectUnits: []
                }
              )
            : null;
        setHostProtectProductsData(hostProtectProductData);
        hostProtectSubTotal = hostProtectProductData ? hostProtectProductData.totalPrice : null;

        const additionalProductData = additionalProducts
          .map(additionalProduct => {
            const product = products.find(p => p._id.toString() === additionalProduct.code.toString());
            if (product) {
              const pricing = calculateAddedProductPricing(product, otherProducts);
              return {
                ...product,
                pricing,
                totalPrice: pricing * additionalProduct.quantity, // Assuming additionalProducts has a quantity field
                quantity: additionalProduct.quantity
              };
            }
            return null;
          })
          .filter(p => p !== null);
        setAddedProducts(additionalProductData);
        addedProductSubTotal = additionalProductData.reduce((acc, product) => acc + product.totalPrice, addedProductSubTotal);

        //Sub Total (before discount)
        subTotal = systemProductSubTotal + addedProductSubTotal + hostProtectSubTotal;
        setSubTotal(subTotal);

        // only trigger if subscription is allow
        if (excludeSystemSubscription === false) {
          if (discountApplied.type === 'amount') {
            systemProductSubTotal = systemProductSubTotal - discountApplied.amount;
            setDiscount(discountApplied);
            setSubTotalAfterDiscount(systemProductSubTotal);
          }
          if (discountApplied.type === 'percentage') {
            systemProductSubTotal = systemProductSubTotal * (1 - discountApplied.amount / 100);
            setDiscount(discountApplied);
            setSubTotalAfterDiscount(systemProductSubTotal);
          }
        }

        //Final SubTotal (after applied discount)
        subTotal = systemProductSubTotal + addedProductSubTotal + hostProtectSubTotal;

        const taxTotal = taxesApplied.reduce((acc, tax) => {
          if (tax.type === 'amount') {
            return acc + tax.amount;
          } else if (tax.type === 'percentage') {
            return acc + subTotal * (tax.amount / 100);
          }
          return acc;
        }, 0);

        // Final Grand Total (SubTotal after TaxTotal)
        const GrandTotal = subTotal + taxTotal;
        setGrandTotal(GrandTotal);
        const inventoryChunks = chunkArray(inventories, 12);
        if (excludeSystemSubscription === false) {
          setInventoryChunks(inventoryChunks);
          setFilteredInventories(inventories);
        } else {
          setFilteredInventories([]);
        }

        //console.log(inventoryChunks, 74);

        //Subscription Description
        if (subscriptionType === 'monthly') {
          setSubscriptionDesc(
            `CHARGES  FOR  ${moment()
              .subtract(1, 'months')
              .format('MMMM YYYY')
              .toUpperCase()}  ACTIVE  UNIT`
          );
        } else if (subscriptionType === 'yearly') {
          setSubscriptionDesc('SYSTEM  SUBSCRIPTION  YEARLY');
        }
      } catch (error) {
        console.error('Error fetching pricing model:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      {!isLoading && isMounted && (
        <BlobProvider
          document={
            hostProtectProductsData ? (
              <HostProtectInvoicePDF
                hostProtectProducts={hostProtectProductsData}
                taxesApplied={taxesApplied}
                subTotal={subTotal}
                subTotalAfterDiscount={subTotalAfterDiscount}
                systemProductSubTotal={systemProductSubTotal}
                date={date}
                invoiceRef={invoiceRef}
                grandTotal={grandTotal}
                discountApplied={discount}
                hostName={hostName}
              />
            ) : (
              <SubscriptionInvoicePDF
                systemProducts={systemProductData}
                additionalProducts={addedProducts}
                modifiedInventories={modifiedInventories}
                taxesApplied={taxesApplied}
                subTotal={subTotal}
                subTotalAfterDiscount={subTotalAfterDiscount}
                systemProductSubTotal={systemProductSubTotal}
                inventories={filteredInventories}
                date={date}
                invoiceRef={invoiceRef}
                remark={remark}
                grandTotal={grandTotal}
                discountApplied={discount}
                inventoryChunks={inventoryChunks}
                hostName={hostName}
                subscriptionDesc={subscriptionDesc}
                subscriptionType={subscriptionType}
                priceTier={priceTier}
                hasParentChildHost={hasParentChildHost}
              />
            )
          }
        >
          {({ blob, url, loading, error }) => {
            if (!loading && url && !isLoading && onGenerate) {
              onGenerate(url, grandTotal);
            }
            return null;
          }}
        </BlobProvider>
      )}
    </>
  );
};

export default InvoicePDF;
